import { useGetFavoriteWorkplaceEntitlement } from "@src/appV2/redesign/ShiftDiscovery/FavoriteWorkplaces/useGetFavoriteWorkplaceEntitlement";
import { useMemo } from "react";

import {
  type GetFavoriteWorkplacesIncludedWorkplace,
  useGetFavoriteWorkplaces,
  type UseGetFavoriteWorkplacesOptions,
} from "./useGetFavoriteWorkplaces";

export interface FavoriteWorkplaceFormatted {
  id: string;
  workplaceId: string;
  workplace?: Pick<GetFavoriteWorkplacesIncludedWorkplace, "id"> &
    GetFavoriteWorkplacesIncludedWorkplace["attributes"];
  openShiftCount: number;
  distanceFromWorker?: number;
}

// FIXME: Automatically disabled during ESLint config update
export interface UseFavoriteWorkplacesOptions extends UseGetFavoriteWorkplacesOptions {}

export function useFavoriteWorkplaces() {
  const { isEntitlementEnabled: isFeatureEnabled } = useGetFavoriteWorkplaceEntitlement();

  const { data, isLoading, isError, error, refetch, isSuccess, status } = useGetFavoriteWorkplaces(
    {},
    { enabled: isFeatureEnabled }
  );

  const favoriteWorkplaces: FavoriteWorkplaceFormatted[] = useMemo(() => {
    const workplaceMap = new Map<string, GetFavoriteWorkplacesIncludedWorkplace>();

    // Create a map of workplace details by ID
    data?.included?.forEach((workplace) => {
      workplaceMap.set(workplace.id, workplace);
    });

    // Merge favorite workplaces with their workplace details
    return (
      data?.data.map((item) => {
        const workplaceId = item.relationships.workplace.data.id;
        const workplace = workplaceMap.get(workplaceId);
        return {
          id: item.id,
          workplaceId,
          openShiftCount: item.attributes.openShiftCount,
          distanceFromWorker: item.attributes.distanceFromWorker,
          workplace: workplace
            ? {
                id: workplace.id,
                name: workplace.attributes.name,
                location: workplace.attributes.location,
              }
            : undefined,
        };
      }) ?? []
    );
  }, [data?.data, data?.included]);

  const favoriteWorkplacesCount = favoriteWorkplaces.length;

  return {
    isEnabled: isFeatureEnabled,
    favoriteWorkplaces,
    favoriteWorkplacesCount,
    isLoading,
    isSuccess,
    isError,
    error,
    status,
    refetch,
  };
}
